import { useState } from 'react';
import type { ResearchModeState, NotificationPrefs } from '../types';
import { requestNotificationPermission } from '../utils/notifications';

interface ResearchDrawerProps {
  isOpen: boolean;
  state: ResearchModeState;
  customerEmail: string | null;
  onClose: () => void;
  onVerifyEmail: () => void;
  onConfirmOtp: (code: string) => void;
  onResendOtp: () => void;
  onToggleMode: (enabled: boolean) => void;
  onUpdateNotificationPrefs: (prefs: NotificationPrefs) => void;
  onOpenContactDrawer: () => void;
}

// Close icon
const CloseIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Check icon for verified state
const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

export function ResearchDrawer({
  isOpen,
  state,
  customerEmail,
  onClose,
  onVerifyEmail,
  onConfirmOtp,
  onResendOtp,
  onToggleMode,
  onUpdateNotificationPrefs,
  onOpenContactDrawer,
}: ResearchDrawerProps) {
  // Determine which view to show
  const noEmail = !customerEmail;
  const emailNotVerified = customerEmail && !state.verified;
  const otpPending = state.otpSent && !state.verified;
  const verified = state.verified;

  return (
    <div
      className={`
        absolute bottom-[10.5rem] right-0 w-72
        bg-white border border-gray-200 rounded-l-lg shadow-xl
        transform transition-transform duration-300 ease-out z-30
        flex flex-col
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <h3 className="text-xs font-medium text-gray-900">Research Mode</h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded"
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3">
        {/* State: No email configured */}
        {noEmail && (
          <NoEmailState onOpenContactDrawer={onOpenContactDrawer} />
        )}

        {/* State: Email not verified */}
        {emailNotVerified && !otpPending && (
          <EmailNotVerifiedState
            email={customerEmail}
            onVerifyEmail={onVerifyEmail}
            isLoading={state.sendingOtp}
            error={state.otpError}
          />
        )}

        {/* State: OTP pending */}
        {otpPending && (
          <OtpInputState
            email={customerEmail!}
            error={state.otpError}
            onConfirmOtp={onConfirmOtp}
            onResendOtp={onResendOtp}
          />
        )}

        {/* State: Verified */}
        {verified && (
          <VerifiedState
            enabled={state.enabled}
            notificationPrefs={state.notificationPrefs}
            onToggleMode={onToggleMode}
            onUpdateNotificationPrefs={onUpdateNotificationPrefs}
          />
        )}
      </div>
    </div>
  );
}

// Sub-components for each state

function NoEmailState({ onOpenContactDrawer }: { onOpenContactDrawer: () => void }) {
  return (
    <div className="text-center py-2">
      <p className="text-xs text-gray-600 mb-3">
        Research mode is available to verified users only.
        <br />
        Please fill in your contact details first.
      </p>
      <button
        onClick={onOpenContactDrawer}
        className="px-4 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
        aria-label="Add contact details"
      >
        Add Contact Details
      </button>
    </div>
  );
}

function EmailNotVerifiedState({
  email,
  onVerifyEmail,
  isLoading,
  error,
}: {
  email: string;
  onVerifyEmail: () => void;
  isLoading?: boolean;
  error?: string | null;
}) {
  return (
    <div className="text-center py-2">
      <p className="text-xs text-gray-600 mb-2">
        Email: <span className="font-medium">{email}</span>
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Verify your email to enable research mode.
      </p>
      {error && (
        <p className="text-xs text-red-500 mb-2">{error}</p>
      )}
      <button
        onClick={onVerifyEmail}
        disabled={isLoading}
        className="px-4 py-1.5 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
        aria-label="Verify email"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Sending...
          </>
        ) : (
          'Verify Email'
        )}
      </button>
    </div>
  );
}

function OtpInputState({
  email,
  error,
  onConfirmOtp,
  onResendOtp,
}: {
  email: string;
  error: string | null;
  onConfirmOtp: (code: string) => void;
  onResendOtp: () => void;
}) {
  const [code, setCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length === 6) {
      onConfirmOtp(code);
    }
  };

  return (
    <div className="py-2">
      <p className="text-xs text-gray-600 mb-2 text-center">
        Enter the 6-digit code sent to
        <br />
        <span className="font-medium">{email}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <input
          type="text"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          className="w-full px-3 py-2 text-center text-lg tracking-widest border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
        />
        {error && (
          <p className="text-xs text-red-500 text-center">{error}</p>
        )}
        <button
          type="submit"
          disabled={code.length !== 6}
          className="w-full px-4 py-1.5 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Verify Code
        </button>
      </form>
      <button
        onClick={onResendOtp}
        className="w-full mt-2 text-xs text-blue-600 hover:underline"
      >
        Resend code
      </button>
    </div>
  );
}

function VerifiedState({
  enabled,
  notificationPrefs,
  onToggleMode,
  onUpdateNotificationPrefs,
}: {
  enabled: boolean;
  notificationPrefs: NotificationPrefs;
  onToggleMode: (enabled: boolean) => void;
  onUpdateNotificationPrefs: (prefs: NotificationPrefs) => void;
}) {
  return (
    <div className="space-y-3">
      {/* Verified badge */}
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <CheckIcon />
        <span>Email verified</span>
      </div>

      {/* Mode toggle */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Research Mode</span>
        <button
          onClick={() => onToggleMode(!enabled)}
          className={`
            relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
            transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${enabled ? 'bg-blue-500' : 'bg-gray-200'}
          `}
          role="switch"
          aria-checked={enabled}
        >
          <span
            className={`
              pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0
              transition duration-200 ease-in-out
              ${enabled ? 'translate-x-4' : 'translate-x-0'}
            `}
          />
        </button>
      </div>

      {/* Explanation */}
      <p className="text-xs text-gray-500">
        {enabled
          ? 'Thorough answers with validation. Responses may take up to 15 minutes.'
          : 'Quick answers with reasonable assumptions.'}
      </p>

      {/* Notification preferences (only when enabled) */}
      {enabled && (
        <div className="pt-2 border-t border-gray-100 space-y-2">
          <p className="text-xs font-medium text-gray-700">
            Notify me when research completes:
          </p>
          <BrowserNotificationCheckbox
            checked={notificationPrefs.browser}
            onChange={(enabled) => onUpdateNotificationPrefs({ ...notificationPrefs, browser: enabled })}
          />
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={notificationPrefs.email}
              onChange={(e) =>
                onUpdateNotificationPrefs({
                  ...notificationPrefs,
                  email: e.target.checked,
                })
              }
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
            Email notification
          </label>
        </div>
      )}
    </div>
  );
}

// Separate component to handle async permission request properly
function BrowserNotificationCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const [isRequesting, setIsRequesting] = useState(false);

  const handleClick = async () => {
    if (checked) {
      // Unchecking - just disable
      onChange(false);
      return;
    }

    // Checking - request permission first
    setIsRequesting(true);
    try {
      const permission = await requestNotificationPermission();
      if (permission === 'granted') {
        onChange(true);
      }
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={() => {}} // Controlled by onClick
        onClick={handleClick}
        disabled={isRequesting}
        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500 cursor-pointer"
      />
      {isRequesting ? 'Requesting permission...' : 'Browser notification'}
    </label>
  );
}
